import { TextField } from '@mui/material'
import React, { memo, useEffect, useRef } from 'react'

const MAX_REASON_INPUT_LENGTH = 255
/** How long typing must pause before the reason reaches the form, so the form validates it as the user types. */
export const REASON_REPORT_DELAY_MS = 300

export type DigitalTwinSettingCommentFieldProps = {
  comment: string
  setComment: (val: string) => void
  disabled: boolean
  errorHelperText?: string
  t: (key: string) => string
}

/**
 * The setting's reason. Typing updates only this field, since re-rendering the whole form on every
 * keystroke made typing lag; the form gets the reason once typing pauses, and on blur.
 */
const DigitalTwinSettingCommentField = memo(function DigitalTwinSettingCommentField({
  comment,
  setComment,
  disabled,
  errorHelperText,
  t,
}: DigitalTwinSettingCommentFieldProps) {
  const [localComment, setLocalComment] = React.useState(comment)
  const reportedComment = useRef(comment)
  const reportTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    if (comment === reportedComment.current) {
      return
    }
    // A reason set from outside, as when another setting is opened: show it, and drop a pending report
    // of the text typed for the previous one.
    clearTimeout(reportTimer.current)
    reportedComment.current = comment
    setLocalComment(comment)
  }, [comment])

  useEffect(() => () => clearTimeout(reportTimer.current), [])

  const report = (value: string): void => {
    clearTimeout(reportTimer.current)
    if (value !== reportedComment.current) {
      reportedComment.current = value
      setComment(value)
    }
  }

  return (
    <TextField
      fullWidth
      margin="normal"
      id={'reason'}
      label={t('Reason') + (localComment.length > 0 ? ` (${localComment.length}/${MAX_REASON_INPUT_LENGTH})` : '')}
      aria-label={'Reason'}
      disabled={disabled}
      required
      variant="outlined"
      value={localComment}
      onChange={(e) => {
        const val = e.target.value as string
        setLocalComment(val)
        clearTimeout(reportTimer.current)
        reportTimer.current = setTimeout(() => report(val), REASON_REPORT_DELAY_MS)
      }}
      onBlur={() => report(localComment)}
      slotProps={{
        // e2e: setting-reason-input — do not remove
        htmlInput: { maxLength: MAX_REASON_INPUT_LENGTH, 'data-testid': 'setting-reason-input' },
      }}
      helperText={errorHelperText}
    />
  )
})

export default DigitalTwinSettingCommentField
